import { HttpClient } from '@angular/common/http';
import { APP_INITIALIZER, Injectable, Provider } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { ShowInfoNotification } from '@platform-frontend/util';
import { MatomoTracker } from 'ngx-matomo-client';
import { Observable, catchError, distinctUntilChanged, map, of, startWith, switchMap, tap } from 'rxjs';
import { YattaLangId } from '../model/yatta-lang-id';
import { MSG_LANGUAGE_NOT_AVAILABLE_GERMAN } from '../notification-messages';
import { LANG_TO_LOCALE_MAPPING } from './lang-to-locale-mapping';
import { FetchAndApplyPreferredLocale, PersistPreferredLocale } from './locale.actions';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class LocaleSetupService {
  constructor(
    private readonly actions$: Actions,
    private readonly http: HttpClient,
    private readonly store: Store,
    private readonly tracker: MatomoTracker,
    private readonly translocoService: TranslocoService
  ) {}

  private langSetByUser: boolean = false;
  private serverLocalePreferenceEnabled: boolean = false;
  private languageNotSupportedNotificationsEnabled: boolean = false;

  init(enableServerLocalePreference: boolean, showLanguageNotSupportedNotifications: boolean, apiUrl: string): void {
    this.serverLocalePreferenceEnabled = enableServerLocalePreference;
    this.languageNotSupportedNotificationsEnabled = showLanguageNotSupportedNotifications;

    this.getListenerOnFetchAndApplyPreferredLocale(apiUrl).subscribe();
    this.setupTrackerOnLangChange().subscribe();
  }

  private getListenerOnFetchAndApplyPreferredLocale(apiUrl: string): Observable<unknown> {
    const localeObservable$: Observable<string | null> = this.serverLocalePreferenceEnabled
      ? this.actions$.pipe(
          ofActionDispatched(FetchAndApplyPreferredLocale),
          switchMap(() => this.http.get<{ preferredLocale: string } | null>(apiUrl + '/master-data/preferred-locale')),
          startWith(null),
          map(response => response?.preferredLocale ?? null),
          catchError(() => of(null))
        )
      : of(null);

    return localeObservable$.pipe(
      distinctUntilChanged(),
      tap(locale => this.setLangFromLocaleObservable(locale)),
      untilDestroyed(this)
    );
  }

  private setupTrackerOnLangChange(): Observable<unknown> {
    return this.translocoService.langChanges$.pipe(
      tap(lang => this.tracker.setCustomDimension(4, lang)),
      untilDestroyed(this)
    );
  }

  private setLangFromLocaleObservable(locale: string | null): void {
    const requestedLang = locale ? this.getLangFromLocale(locale) : null;
    if (requestedLang) {
      this.translocoService.setActiveLang(requestedLang);
      this.langSetByUser = true;
    }
  }

  setLocaleByApi(locale: string): void {
    if (!this.langSetByUser) {
      const requestedLang = this.getLangFromLocale(locale);
      if (requestedLang) this.translocoService.setActiveLang(requestedLang);
    }
  }

  setLocaleByUser(locale: string): void {
    const requestedLang = this.getLangFromLocale(locale);
    if (requestedLang) {
      if (this.serverLocalePreferenceEnabled) {
        this.store.dispatch(new PersistPreferredLocale(locale));
      }
      this.translocoService.setActiveLang(requestedLang);
      this.langSetByUser = true;
    }
  }

  isLocaleSupported(locale: string): boolean {
    return this.getLangFromLocale(locale) !== null;
  }

  private getLangFromLocale(locale: string): YattaLangId | null {
    const localeIndex = Object.values(LANG_TO_LOCALE_MAPPING).findIndex(loc => loc.startsWith(locale));
    if (localeIndex >= 0) {
      const requestedLang = Object.keys(LANG_TO_LOCALE_MAPPING)[localeIndex] as YattaLangId;
      if (this.isLangAvailable(requestedLang)) return requestedLang;
      else if (requestedLang === 'de' && this.languageNotSupportedNotificationsEnabled)
        this.store.dispatch(new ShowInfoNotification(MSG_LANGUAGE_NOT_AVAILABLE_GERMAN));
    }
    return null;
  }

  private isLangAvailable(requestedLang: string | null): requestedLang is string {
    return this.translocoService.getAvailableLangs().findIndex(lang => (typeof lang === 'string' ? lang === requestedLang : lang.id === requestedLang)) >= 0;
  }
}

export const LocaleSetupServiceInitializer: (
  enableServerLocalePreference: boolean,
  showLanguageNotSupportedNotifications: boolean,
  apiUrl: string
) => Provider = (showLanguageNotSupportedNotifications: boolean, enableServerLocalePreference: boolean, apiUrl: string) => ({
  provide: APP_INITIALIZER,
  useFactory: (localeSetupService: LocaleSetupService) => () =>
    localeSetupService.init(enableServerLocalePreference, showLanguageNotSupportedNotifications, apiUrl),
  deps: [LocaleSetupService],
  multi: true
});
